import { Box, ContextMenu, Flex, Text } from '@radix-ui/themes';
import { verboseDebug } from 'classes/helpers/debug.helper';
import { ClickCatcher } from 'components/common/layout/click-catcher';
import { SEPARATOR_LABEL } from 'components/common/menu';
import { convertTableToMenu } from 'components/common/table/actions-cells';
import { t } from 'i18next';
import { IVerboseComponent } from 'interfaces/i-components';
import { ITableAction } from 'interfaces/tables/action';
import { RowWithID } from 'interfaces/tables/main';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { createContext, FC, ReactNode, useMemo, useState } from 'react';

const CONTEXT_NAME = 'ContextMenuContext';

interface IProps {
  // these actions require a target
  targetActions: ITableAction[];
  children: ReactNode;
}

export interface IContextMenuContext {
  target: RowWithID | undefined;
  readonly setTarget: (v: RowWithID | undefined) => void;
}

const DEFAULT: IContextMenuContext = {
  target: undefined,
  setTarget: () => console.error(`${CONTEXT_NAME}: not init`),
};

export const ContextMenuContext = createContext(DEFAULT);

/** for tracking misc details across entire app */
export const ContextMenuProvider: FC<IProps & IVerboseComponent> = (props) => {
  const [target, setTarget] = useState<RowWithID>();

  // these actions are resolved table actions using the target
  const menuActions = useMemo(() => {
    if (!target) {
      verboseDebug(props, 'create menu actions > no target');
      return [];
    }

    const output = props.targetActions
      .map((a) => convertTableToMenu(a, target))
      .filter((m) => !m.invisible);

    verboseDebug(props, 'create menu actions', output);

    return output;
  }, [target, props.targetActions]);

  const state: IContextMenuContext = {
    target: target,
    setTarget: setTarget,
  };

  return (
    <ContextMenuContext.Provider value={state}>
      <ClickCatcher>
        <ContextMenu.Root
          onOpenChange={(open) => {
            if (open) {
              // do nothing if the menu just opened
              verboseDebug(props, 'menu opened > no action');
              return;
            }

            // this is for the case where the menu was closed and no new menu was opened, then reset the target so the highlight goes away
            // this should occur before any context event of right-clicking a new row goes through (which would select a new target) because of the timeout
            // this ensures that clearing things here will not interfere with selecting a new target for the new row
            verboseDebug(props, 'menu closed > context menu target cleared');
            setTarget(undefined);
          }}
        >
          <ContextMenu.Trigger>
            {/* for some reason, the context menu won't open/trigger w/o this div */}
            <div>{props.children}</div>
          </ContextMenu.Trigger>

          {target && menuActions.length > 0 && (
            <ContextMenu.Content
              key={target._id}
              className="MenuContent scroll-hover"
            >
              {menuActions.map((a, i) => {
                if (a.label === SEPARATOR_LABEL) {
                  return (
                    <ContextMenu.Separator
                      key={i}
                      data-group={a.group}
                      className="MenuSeparator"
                      // avoid starting a menu with a separator
                      hidden={i === 0}
                    />
                  );
                }

                return (
                  <ContextMenu.Item
                    key={i}
                    className={StringHelper.classNames([
                      'MenuItem',
                      a.className,
                    ])}
                    data-group={a.group}
                    title={a.tooltip ? t(a.tooltip).toString() : undefined}
                    disabled={a.disabled}
                    onClick={a.onClick}
                  >
                    {a.prefixIcon && (
                      <Box
                        className="valign-center"
                        style={{
                          marginRight: '8px',
                        }}
                      >
                        <Text
                          color={a.disabled ? RADIX.COLOR.SECONDARY : a.color}
                        >
                          <Flex align="center">{a.prefixIcon}</Flex>
                        </Text>
                      </Box>
                    )}

                    <Box className="valign-center">
                      <Text
                        color={a.disabled ? RADIX.COLOR.SECONDARY : a.color}
                        title={t(a.label).toString()}
                        truncate
                      >
                        {t(a.label)}
                      </Text>
                    </Box>

                    {a.suffixIcon && (
                      <Box
                        className="valign-center"
                        style={{
                          marginLeft: '8px',
                          paddingTop: '2px',
                        }}
                      >
                        <Flex align="center">{a.suffixIcon}</Flex>
                      </Box>
                    )}
                  </ContextMenu.Item>
                );
              })}
            </ContextMenu.Content>
          )}
        </ContextMenu.Root>
      </ClickCatcher>
    </ContextMenuContext.Provider>
  );
};
